(ns hai.contacts.form
  "The contact composer every contact editor shares: the name as a heading
   (click it to edit) beside the avatar, one row per email, and dashed pills
   for the type, GitHub and LinkedIn — each staging locally and all reaching
   `on-save` together, in one payload. Nothing auto-saves. Mirrors
   [[hai.notes.form]] and [[hai.tasks.form]]."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as helpers]
   [hai.contacts.mutations :as mutations]
   [hai.entity.draft :as draft]
   [hai.entity.form :as entity-form]
   [hai.entity.sections :as sections]
   [hai.ui.icons :as icons]
   [hai.ui.keyboard :as keyboard]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defn from-contact
  "A contact as a draft payload: every field the composer stages. What it is
   seeded with, and what it measures having something to save against."
  [contact]
  {:full-name       (or (:contact/full-name contact) "")
   :emails          (vec (sort (:contact/email contact)))
   :organisation?   (helpers/org? contact)
   :github-login    (or (:contact/github-login contact) "")
   :linkedin-url    (or (:contact/linkedin-url contact) "")
   :mailing-domains (vec (sort (:contact/org-mailing-namespace contact)))})

(defn- as-filed
  "The draft as a save would file it: typed values trimmed, blank rows
   dropped, lists in a fixed order — so a blank row or stray whitespace is
   nothing to save."
  [{:keys [full-name emails github-login linkedin-url mailing-domains] :as draft}]
  (let [trimmed-list (fn [values] (vec (sort (remove str/blank? (map str/trim values)))))]
    (assoc draft
           :full-name       (str/trim full-name)
           :github-login    (str/trim github-login)
           :linkedin-url    (str/trim linkedin-url)
           :emails          (trimmed-list emails)
           :mailing-domains (trimmed-list mailing-domains))))

(defn unfiled?
  "Whether `draft` holds anything the contact as filed doesn't — what enables
   Save and Cancel."
  [draft contact]
  (not= (as-filed draft) (as-filed (from-contact contact))))

;; The white input chrome the task composer's title wears, shared by the name
;; and the list rows.
(o/defstyled input-wrap :div
  {:flex "1 1 auto" :min-width "0"
   :display "flex" :align-items "stretch"
   :background "#FFFFFF"
   :border-width "1px" :border-style "solid" :border-color t/--property-3
   :border-radius "12px"
   :overflow "hidden"
   :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
   :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
  [:&:focus-within {:border-color t/--card-sage
                    :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]
  [:input
   {:flex "1" :min-width "0"
    :background "transparent" :border "none" :outline "none"
    :font-family "inherit" :line-height "1.4"
    :color t/--card-forest}
   [:&::placeholder {:color t/--link-muted :font-weight "400"}]])

;; Individual | Organisation inside one dashed pill — the task composer's
;; urgency pill, led by a label instead of a glyph.
(o/defstyled type-pill :span
  {:display "inline-flex" :align-items "center" :gap "2px"
   :box-sizing "border-box" :height "24px" :padding "0 3px"
   :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
   :border-radius "7px"}
  [:.pill-label
   {:padding "0 6px 0 8px"
    :font-size "10px" :font-weight "700" :letter-spacing "0.08em"
    :text-transform "uppercase"
    :color t/--link-muted}]
  ;; Both segments carry the outline; only the picked one colours it in, so
  ;; the pill never shifts as the pick moves.
  [:button
   {:height "18px" :padding "0 8px"
    :border-width "1px" :border-style "solid" :border-color "transparent"
    :border-radius "5px"
    :background "transparent"
    :font-family "inherit" :font-size "12px" :font-weight "600" :line-height "1"
    :color "rgba(74,93,63,0.82)"
    :cursor "pointer"
    :transition "background 0.15s"}
   [:&:hover {:background "rgba(50,64,37,0.06)"}]
   [:&.active {:border-color t/--note-sage-55 :color t/--card-forest}]])

;; A dashed pill holding a field's icon and an inline input — GitHub and
;; LinkedIn. Empty it reads as an invitation; carrying a value it reads as a
;; chip (`.filled`).
(o/defstyled field-pill :label
  {:display "inline-flex" :align-items "center" :gap "6px"
   :box-sizing "border-box" :height "24px" :padding "0 10px"
   :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
   :border-radius "7px"
   :color "rgba(74,93,63,0.82)"
   :cursor "text"
   :transition "border-color 0.15s, background 0.15s, box-shadow 0.15s"}
  [:svg {:width "14px" :height "14px" :fill "currentColor" :flex "0 0 auto"}]
  [:input
   {:min-width "12ch" :max-width "32ch"
    :field-sizing "content"
    :background "transparent" :border "none" :outline "none" :padding "0"
    :font-family "inherit" :font-size "12px" :font-weight "600" :line-height "1"
    :color t/--card-forest}
   [:&::placeholder {:color "rgba(74,93,63,0.6)" :font-weight "500"}]]
  [:&.filled {:border-style "solid" :border-color t/--note-sage-55}]
  ;; Editing: the white surface and sage ring the name and email inputs take.
  [:&:focus-within
   {:background "#FFFFFF"
    :border-style "solid" :border-color t/--card-sage
    :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]
  ;; A LinkedIn URL is mostly its fixed `https://linkedin.com/in/` prefix: at
  ;; rest the input hugs what it holds, and editing opens it up to room for
  ;; the prefix and a handle.
  [:&.linkedin
   [:input {:max-width "52ch"}]
   [:&:focus-within [:input {:min-width "38ch"}]]]
  ;; A narrow screen can't fit that beside its gutters: at rest the pill still
  ;; hugs its value but never past the row, and editing takes the whole row.
  [:at-media {:max-width "480px"}
   [:&.linkedin
    {:max-width "100%" :box-sizing "border-box"}
    [:input {:min-width "0"}]
    [:&:focus-within
     {:align-self "stretch"}
     [:input {:flex "1 1 auto" :max-width "none"}]]]])

(o/defstyled shell :div
  {:display "flex" :flex-direction "column" :gap "16px"}
  [:.name-row {:display "flex" :align-items "center"}]
  ;; The committed name, a heading; clicking it swaps the input back in.
  [:.name-display
   {:flex "1 1 auto" :min-width "0" :margin "0"
    :font-size "26px" :font-weight "700" :line-height "1.2"
    :letter-spacing "-0.01em"
    :color t/--card-forest
    :overflow-wrap "anywhere"
    :cursor "text"}]
  [:.name-input {:padding "10px 14px" :font-size "20px" :font-weight "700"}]
  ;; One row per email or domain: the field's icon, the input, and its ✕.
  [:.list-rows {:display "flex" :flex-direction "column" :align-items "flex-start" :gap "8px"}]
  [:.list-row {:align-self "stretch" :display "flex" :align-items "center" :gap "10px"}]
  [:.row-icon
   {:display "inline-flex" :align-items "center" :flex "0 0 auto"
    :color t/--link-muted}
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]]
  [:.row-input {:padding "11px 14px" :font-size "15px"}]
  [:.row-remove
   {:width "32px" :height "32px" :padding "0" :flex "0 0 auto"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "999px" :background "transparent"
    :color t/--link-muted
    :cursor "pointer"
    :transition "background 0.15s ease, color 0.15s ease"}
   [:&:hover {:background "rgba(50,64,37,0.08)" :color t/--card-forest}]
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]]
  [sections/add-section-pill {:box-sizing "border-box" :height "24px" :align-self "flex-start"}]
  [:.pill-rows {:display "flex" :flex-direction "column" :align-items "flex-start" :gap "10px"}]
  ;; Where the calendar sync found the contact — provenance, read-only.
  [:.provenance
   {:display "inline-flex" :align-items "center" :gap "6px"
    :font-size "12px" :color t/--link-muted}
   [:svg {:width "14px" :height "14px" :flex "0 0 auto"}]])

(defn- list-rows
  "One input per value with its ✕, over a ＋ pill that appends a row and
   focuses it."
  [{:keys [values icon placeholder add-label remove-label on-change]}]
  (r/with-let [!focus-new? (r/atom false)]
    (let [focus-new? @!focus-new?
          last-index (dec (count values))]
      [:div.list-rows
       (doall
        (for [[index value] (map-indexed vector values)]
          ^{:key index}
          [:div.list-row
           [:span.row-icon icon]
           [input-wrap
            [:input.row-input
             {:type          "text"
              :auto-complete "off"
              :value         value
              :placeholder   placeholder
              :aria-label    placeholder
              :auto-focus    (and (= index last-index) focus-new?)
              :on-blur     #(reset! !focus-new? false)
              :on-change   (fn [e] (on-change (assoc values index (.. e -target -value))))}]]
           [:button.row-remove
            {:type       "button"
             :aria-label remove-label
             :title      remove-label
             :on-click   (fn [_]
                           (on-change (vec (concat (subvec values 0 index)
                                                   (subvec values (inc index))))))}
            [icons/outlined-close {:size 18}]]]))
       [sections/add-section-pill
        {:type          "button"
         :on-mouse-down #(.preventDefault %)
         :on-click      (fn [_]
                          (reset! !focus-new? true)
                          (on-change (conj values "")))}
        [:span.plus "＋"] add-label]])))

(defn- blur-on-commit
  "↵ and Escape leave the field — the heading takes over from the input."
  [e]
  (case (.-key e)
    ("Enter" "Escape") (do (.preventDefault e) (.. e -target blur))
    nil))

(defn contact-form
  "The composer body. Everything it stages lives in the draft at `draft-id`
   ([[hai.entity.draft]]), which the host seeds; `contact` is the contact as
   filed, what the draft is measured against to know whether there is
   anything to save.

   There is one save, from the Save button or ⌘/Ctrl+↵ in any field — the
   button carries the chord as key caps (`show-hint?`; off for touch). It
   calls `on-save` with the staged fields; the host closes or re-seeds from
   there. `saving?` is the host's in-flight state — Save disables and spins
   while it holds. Save and Cancel are always there, disabled while the
   composer holds the contact exactly as it stands — Save also while the
   contact names nobody, which the create mutation refuses. `on-cancel` is
   what Cancel does — put the draft back to the contact as filed."
  [{:keys [draft-id contact auto-focus? show-hint? saving? on-save on-cancel]}]
  (r/with-let [!editing-name? (r/atom false)
               change! (fn [changes] (>e [::draft/change draft-id changes]))]
    (let [{:keys [full-name emails organisation? github-login linkedin-url mailing-domains]
           :as draft}
          @(<s [::draft/draft draft-id])
          unfiled? (unfiled? draft contact)
          blank?   (mutations/blank-payload? draft)
          save!    (fn []
                     (when (and unfiled? (not blank?) (not saving?))
                       (on-save draft)))]
      [shell
       {:on-key-down (fn [e]
                       (when (keyboard/submit-shortcut? e)
                         (.preventDefault e)
                         (save!)))}
       [:div.name-row
        ;; A blank name has no heading to show, so it stays an input.
        (if (or @!editing-name? (str/blank? full-name))
          [input-wrap
           [:input.name-input
            {:type          "text"
             :auto-complete "off"
             :value         full-name
             :placeholder "Name"
             :aria-label  "Name"
             :auto-focus  (or auto-focus? @!editing-name?)
             :on-change   #(change! {:full-name (.. % -target -value)})
             :on-focus    #(reset! !editing-name? true)
             :on-blur     #(reset! !editing-name? false)
             :on-key-down blur-on-commit}]]
          [:h2.name-display {:on-click #(reset! !editing-name? true)} full-name])]
       [list-rows
        {:values       emails
         :icon         [icons/outlined-email]
         :placeholder  "Email"
         :add-label    "Add email"
         :remove-label "Remove email"
         :on-change    #(change! {:emails %})}]
       [:div.pill-rows
        [type-pill
         [:span.pill-label "Type"]
         [:button {:type          "button"
                   :class         (when-not organisation? "active")
                   :on-mouse-down #(.preventDefault %)
                   :on-click      #(change! {:organisation? false})}
          "Individual"]
         [:button {:type          "button"
                   :class         (when organisation? "active")
                   :on-mouse-down #(.preventDefault %)
                   :on-click      #(change! {:organisation? true})}
          "Organisation"]]
        [field-pill {:class (when-not (str/blank? github-login) "filled")}
         [icons/github {:size 14}]
         [:input {:type          "text"
                  :auto-complete "off"
                  :value         github-login
                  :placeholder "GitHub handle"
                  :aria-label  "GitHub handle"
                  :on-change   #(change! {:github-login (.. % -target -value)})}]]
        [field-pill {:class ["linkedin" (when-not (str/blank? linkedin-url) "filled")]}
         [icons/outlined-linkedin]
         [:input {:type          "text"
                  :auto-complete "off"
                  :value         linkedin-url
                  :placeholder "linkedin.com/in/…"
                  :aria-label  "LinkedIn URL"
                  :on-change   #(change! {:linkedin-url (.. % -target -value)})}]]]
       (when organisation?
         [list-rows
          {:values       mailing-domains
           :icon         [icons/outlined-public]
           :placeholder  "Mailing domain"
           :add-label    "Add domain"
           :remove-label "Remove domain"
           :on-change    #(change! {:mailing-domains %})}])
       (when-let [source (:contact/auto-filled-from-connection-email contact)]
         [:div.provenance [icons/calendar] "Auto-added from " source])
       [entity-form/footer
        [entity-form/actions
         {:unfiled?       unfiled?
          :saving?        saving?
          :save-disabled? blank?
          :shortcut       (when show-hint? (entity-form/chord "↵"))
          :on-cancel      on-cancel
          :on-save        save!}]]])))
